import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { confirmNew, getPayableOrderTransactions, initiateNew, startCheckout, isRecoverableFlow } from '..'
import { checkoutErrors, RecoverableCurrentError } from '../../model'
import { RecoverableTerminalErrorStatus } from '@afterpay/types'
import { PURGE } from 'redux-persist'
import { getOverduePayments, initiateInstallmentPayment } from '../paymentSchedule/apis'
import { OrderResponse, OverduePaymentResponse } from '@afterpaytouch/portal-api/types'
import { isPortalApiError } from '@afterpaytouch/portal-api'

export interface RecoverableTerminalErrorsState {
  status: RecoverableTerminalErrorStatus
  traceId: string
  isPageReady: boolean
  recovered: boolean
  error?: RecoverableCurrentError
  selectedOrder?: OrderResponse | OverduePaymentResponse
}

export const recoverableErrorsInitialState: RecoverableTerminalErrorsState = {
  status: RecoverableTerminalErrorStatus.NotStarted,
  traceId: '',
  isPageReady: false,
  recovered: false,
}

const slice = createSlice({
  name: 'recoverableTerminalErrors',
  initialState: recoverableErrorsInitialState,
  reducers: {
    setRecoverableFlowTraceId(state: RecoverableTerminalErrorsState, action: PayloadAction<string>) {
      state.traceId = action.payload
    },
    setRecoverableFlowStatus(state: RecoverableTerminalErrorsState, action: PayloadAction<RecoverableTerminalErrorStatus>) {
      state.status = action.payload
    },
    resetRecoverableFlowError(state: RecoverableTerminalErrorsState) {
      state.error = undefined
    },
    setRecoverableFlowSelectedOrder(state: RecoverableTerminalErrorsState, action: PayloadAction<OrderResponse | OverduePaymentResponse>) {
      state.selectedOrder = action.payload
    },
    setRecoverableFlowPageReady(state: RecoverableTerminalErrorsState, action: PayloadAction<boolean>) {
      state.isPageReady = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(PURGE, () => recoverableErrorsInitialState)
      .addMatcher(startCheckout.matchPending, (state) => {
        state.recovered = state.status === RecoverableTerminalErrorStatus.Finished
        state.status = RecoverableTerminalErrorStatus.NotStarted
        state.isPageReady = false
        state.selectedOrder = undefined
      })
      .addMatcher(getOverduePayments.matchFulfilled, (state, action) => {
        if (isRecoverableFlow(state.status)) {
          state.status = RecoverableTerminalErrorStatus.FetchedQuery
          state.selectedOrder = action.payload
        }
      })
      .addMatcher(getPayableOrderTransactions.matchFulfilled, (state, action) => {
        if (!isRecoverableFlow(state.status)) {
          return
        }

        state.status = RecoverableTerminalErrorStatus.FetchedQuery

        if (action.payload.length === 0) {
          state.status = RecoverableTerminalErrorStatus.LackOfEligibleOrders
        }
        state.selectedOrder = action.payload[0]
      })
      .addMatcher(initiateInstallmentPayment.matchPending, (state) => {
        if (isRecoverableFlow(state.status)) {
          state.status = RecoverableTerminalErrorStatus.InitiatingInstallmentPayment
        }
      })
      .addMatcher(initiateInstallmentPayment.matchFulfilled, (state) => {
        if (isRecoverableFlow(state.status)) {
          state.status = RecoverableTerminalErrorStatus.InitiatedInstallmentPayment
        }
      })
      .addMatcher(initiateNew.matchPending, (state) => {
        state.error = undefined
      })
      .addMatcher(initiateNew.matchRejected, (state) => {
        if (isRecoverableFlow(state.status)) {
          state.error = {
            error: 'unknown',
          }
        }
      })
      .addMatcher(confirmNew.matchPending, (state) => {
        state.error = undefined
      })
      .addMatcher(confirmNew.matchRejected, (state, action) => {
        if (isRecoverableFlow(state.status)) {
          state.error = {
            error: 'unknown',
          }
          if (action.meta.rejectedWithValue) {
            const errorCode = isPortalApiError(action.payload?.data) ? action.payload?.data.errorCode : undefined

            if (errorCode !== undefined) {
              state.error = {
                error: checkoutErrors[errorCode] ?? errorCode,
                errorCode,
              }
            }
          }
        }
      })
  },
})

export const { setRecoverableFlowTraceId, setRecoverableFlowStatus, resetRecoverableFlowError, setRecoverableFlowSelectedOrder, setRecoverableFlowPageReady } =
  slice.actions

export default slice.reducer
